import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock, CreditCard, Loader2, Check, BellRing } from "lucide-react";
import { Reveal, SectionLabel } from "@/lib/Reveal";
import { SERVICES, API_BASE } from "@/lib/data";

const SLOTS = [
  { id: "morning", label: "Morning", time: "08:00 – 12:00" },
  { id: "afternoon", label: "Afternoon", time: "12:00 – 17:00" },
  { id: "evening", label: "Evening", time: "17:00 – 20:00" },
];

const fmt = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const Booking = ({ preselected }) => {
  const [serviceId, setServiceId] = useState("boiler_service");
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", postcode: "", notes: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preselected) setServiceId(preselected);
  }, [preselected]);

  const days = useMemo(() => {
    const out = [];
    const d = new Date();
    while (out.length < 14) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0) out.push(new Date(d));
    }
    return out;
  }, []);

  const service = SERVICES.find((s) => s.id === serviceId);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!date || !slot) {
      toast.error("Pick a date and time slot first");
      return;
    }
    if (!form.name || !form.phone || !form.email || !form.postcode) {
      toast.error("Please fill in your name, phone, email and postcode");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/bookings/checkout`, {
        service_id: serviceId,
        date: fmt(date),
        slot: SLOTS.find((s) => s.id === slot).label + " " + SLOTS.find((s) => s.id === slot).time,
        ...form,
        origin_url: window.location.origin,
      });
      const inFrame = window.self !== window.top;
      if (inFrame) {
        const win = window.open(data.checkout_url, "_blank");
        if (win) {
          toast.success(`Booking ${data.booking_ref} created — finish payment in the new tab that just opened`);
          setLoading(false);
          return;
        }
      }
      toast.success(`Booking ${data.booking_ref} created — redirecting to secure payment`);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong — please call us instead");
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#0E0E0E] border border-neutral-800 focus:border-torch outline-none rounded-sm px-4 py-3 text-sm text-white placeholder:text-neutral-600 transition-colors";

  return (
    <section id="booking" data-testid="booking-section" className="py-20 lg:py-28 bg-[#0C0C0C] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal><SectionLabel>04 / Book & pay online</SectionLabel></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white mt-4 mb-14">
            Lock in your slot.
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <Reveal className="space-y-8">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase mb-4">1 · Choose service</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {SERVICES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setServiceId(s.id)}
                    data-testid={`booking-service-${s.id}`}
                    className={`text-left border rounded-sm px-4 py-4 transition-all ${
                      serviceId === s.id
                        ? "border-torch bg-torch/10"
                        : "border-neutral-800 bg-surface hover:border-neutral-600"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-display font-bold text-sm text-white leading-tight">{s.name}</span>
                      {serviceId === s.id && <Check className="w-4 h-4 text-torch shrink-0" />}
                    </div>
                    <span className="font-mono text-xs text-torch mt-1 block">{s.priceLabel}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase mb-4 flex items-center gap-2">
                <CalendarDays className="w-3.5 h-3.5" /> 2 · Pick a day
              </p>
              <div className="grid grid-cols-7 gap-2">
                {days.map((d) => {
                  const active = date && fmt(d) === fmt(date);
                  return (
                    <button
                      key={fmt(d)}
                      onClick={() => setDate(d)}
                      data-testid={`booking-day-${fmt(d)}`}
                      className={`border rounded-sm py-2.5 text-center transition-all ${
                        active ? "border-torch bg-torch text-white" : "border-neutral-800 bg-surface hover:border-neutral-600 text-neutral-300"
                      }`}
                    >
                      <span className="block font-mono text-[9px] uppercase tracking-wider opacity-70">
                        {d.toLocaleDateString("en-GB", { weekday: "short" })}
                      </span>
                      <span className="block font-display font-bold text-sm">{d.getDate()}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase mb-4 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> 3 · Pick a window
              </p>
              <div className="grid grid-cols-3 gap-3">
                {SLOTS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSlot(s.id)}
                    data-testid={`booking-slot-${s.id}`}
                    className={`border rounded-sm px-3 py-3.5 transition-all ${
                      slot === s.id ? "border-torch bg-torch/10" : "border-neutral-800 bg-surface hover:border-neutral-600"
                    }`}
                  >
                    <span className="block font-display font-bold text-sm text-white">{s.label}</span>
                    <span className="block font-mono text-[10px] text-neutral-500 mt-1">{s.time}</span>
                  </button>
                ))}
              </div>
              {date && slot && (
                <div data-testid="booking-confirm-note" className="mt-4 flex items-start gap-3 border border-torch/30 bg-torch/5 rounded-sm px-4 py-3.5">
                  <BellRing className="w-4 h-4 text-torch shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-300 leading-relaxed">
                    All appointment requests are <span className="text-white font-semibold">confirmed as soon as possible</span> — once you've paid, we'll text or email to confirm your exact slot.
                  </p>
                </div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="bg-surface border border-neutral-800 rounded-sm p-6 sm:p-8 lg:sticky lg:top-28">
              <p className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase mb-6">4 · Your details</p>
              <div className="space-y-4">
                <input data-testid="booking-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="booking-phone-input" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
                  <input data-testid="booking-postcode-input" className={inputCls} placeholder="Postcode" value={form.postcode} onChange={set("postcode")} />
                </div>
                <input data-testid="booking-email-input" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={set("email")} />
                <textarea
                  data-testid="booking-notes-input"
                  className={`${inputCls} resize-none`}
                  rows={3}
                  placeholder="Anything we should know? (boiler make, error code, access notes…)"
                  value={form.notes}
                  onChange={set("notes")}
                />
              </div>

              <div className="mt-8 border-t border-neutral-800 pt-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-400">{service.name}</p>
                  <p className="font-mono text-[10px] tracking-wider text-neutral-500 uppercase mt-1">
                    {date ? date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "Pick a date"}
                    {slot ? ` · ${SLOTS.find((s) => s.id === slot).label}` : ""}
                  </p>
                </div>
                <span data-testid="booking-total" className="font-mono font-bold text-3xl text-torch">{service.priceLabel}</span>
              </div>

              <button
                onClick={submit}
                disabled={loading}
                data-testid="booking-pay-btn"
                className="mt-6 w-full bg-torch hover:bg-torch-hover disabled:opacity-60 text-white font-display font-bold uppercase tracking-wide py-4 rounded-sm flex items-center justify-center gap-3 transition-all hover:shadow-[0_0_32px_rgba(255,85,0,0.45)]"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                {loading ? "Preparing secure checkout…" : `Pay ${service.priceLabel} & confirm booking`}
              </button>
              <p className="mt-4 font-mono text-[10px] tracking-wider text-neutral-600 uppercase text-center">
                Secure card payment via Stripe · Confirmation sent by email
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
